import { type VariantProps, cva } from "class-variance-authority";
import clsx from "clsx";
import type { JSX } from "react";
import { Trans } from "react-i18next";

const typographyStyles = cva("", {
  variants: {
    variant: {
      h1: "text-heading1",
      h2: "text-heading2",
      h3: "text-heading3",
      h4: "text-heading4",
      h5: "text-heading5",
      h6: "text-heading6",
      body: "",
      caption: "text-caption",
      overline: "overlined",
      bold: "font-bold",
    },
    darken: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: ["h1", "h2", "h3", "h4", "h5", "h6"],
      darken: true,
      class: "text-blue-900",
    },
  ],
  defaultVariants: {
    variant: "body",
    darken: false,
  },
});

const variantToTag: Record<string, keyof JSX.IntrinsicElements> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  body: "p",
  caption: "span",
  overline: "span",
  bold: "strong",
} as const;

type TypographyProps = VariantProps<typeof typographyStyles> & {
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  children?: React.ReactNode;
  trans?: string;
  transParams?: Record<string, string | number>;
  darken?: boolean;
};

export const Typography: React.FC<TypographyProps> = ({
  as,
  variant = "body",
  className,
  children,
  trans,
  transParams,
  darken = false,
  ...props
}) => {
  const Component: keyof JSX.IntrinsicElements =
    as || variantToTag[variant as keyof typeof variantToTag] || "span";
  return (
    <Component
      className={clsx(typographyStyles({ variant, darken }), className)}
      {...props}
    >
      {trans ? (
        <Trans
          i18nKey={trans}
          values={transParams}
          components={{
            body: <Typography variant={"body"} />,
            caption: <Typography variant={"caption"} />,
            overline: <Typography variant={"overline"} />,
            h1: (
              <Typography className="text-heading1 font-semibold" as={"span"} />
            ),
            h2: (
              <Typography className="text-heading2 font-semibold" as={"span"} />
            ),
            h3: (
              <Typography className="text-heading3 font-semibold" as={"span"} />
            ),
            h4: (
              <Typography className="text-heading4 font-semibold" as={"span"} />
            ),
            h5: (
              <Typography className="text-heading5 font-semibold" as={"span"} />
            ),
            h6: (
              <Typography className="text-heading6 font-semibold" as={"span"} />
            ),
            bold: <Typography className="font-bold" as={"span"} />,
          }}
        />
      ) : (
        children
      )}
    </Component>
  );
};
